<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <div style="max-width:560px">
        <h3>Rediger bruker</h3>
        <br/>
        <div>
          <span class="ingredient-title">Fornavn</span>
          <input id="firstname" class="form-control" type="text" v-model="user.firstname"/>
        </div>

        <div>
          <span class="ingredient-title">Etternavn</span>
          <input id="lastname" class="form-control" type="text" v-model="user.lastname"/>
        </div>

        <div>
          <span class="ingredient-title">Rolle</span>
          <select class="form-select" v-model="user.userRole">
            <option value="">Velg rolle</option>
            <option value="EMPLOYEE">Ansatt</option>
            <!-- Read-only role: may view and download reports only -->
            <option value="ACCOUNTANT">Regnskapsfører</option>
            <!-- Only super admin can create merchant admin user -->
            <option v-if="isSuperAdmin()" value="ADMIN">Admin</option>
          </select>
        </div>

        <div>
          <span class="ingredient-title">Brukernavn</span>
          <input id="username" class="form-control" type="text" v-model="user.username"/>
        </div>

        <div>
          <span class="ingredient-title">Passord</span>
          <input id="userPassword" class="form-control" type="password" autocomplete="new-password"
                 placeholder="La stå tomt for å beholde passordet" v-model="user.userPassword"/>
          <div class="mt-1" style="font-size:0.85rem; color:#666">
            Fyll ut begge feltene bare hvis passordet skal endres.
          </div>
        </div>

        <div>
          <span class="ingredient-title">Gjenta passord</span>
          <input id="confirmPassword" class="form-control" type="password" autocomplete="new-password"
                 v-model="confirmPassword"/>
        </div>
        <div v-if="user.userPassword !== confirmPassword">
          <span class="validation-failed">(Passordene er ikke like)</span>
        </div>

        <!-- Pinkode is only used for POS login; ACCOUNTANT is a backoffice read-only role and doesn't need it. -->
        <template v-if="user.userRole !== 'ACCOUNTANT'">
          <div>
            <span class="ingredient-title">Pinkode(4 sifre)</span>
            <input id="pincode" class="form-control" type="text" inputmode="numeric" maxlength="4" v-model="user.pincode" @input="checkUserPincode" />
          </div>
          <div v-if="!validPin" class="form-group">
            <span class="validation-failed">(Pin-koden er allerede i bruk)</span>
          </div>
        </template>

        <div>
          <span class="ingredient-title">Epost</span>
          <input id="email" class="form-control" type="text" v-model="user.email" @input="validateEmail" />
        </div>
        <div v-if="!validEmail">
          <span className="validation-failed">(Ugyldig epost)</span>
        </div>

        <div>
          <span class="ingredient-title">Telefon</span>
          <input id="phone" class="form-control" type="tel" inputmode="tel" v-model.trim="user.phone"/>
        </div>

        <div class="bo_hr mt-4">
          <hr/>
        </div>

        <div class="mb-5">
          <div v-if="saveError" class="alert alert-danger" role="alert">{{ saveError }}</div>

          <button v-if="activateSaveButton" type="button" class="bo_btn" @click="saveUser"> Lagre </button>
          <button v-else type="button" class="bo_btn" @click="saveUser" disabled="true"> Lagre </button>
        </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import UserUtil from "@/util/UserUtil";
import UserService from "@/services/user/UserService";

/**
 * Why a save can now be refused.
 *
 * A password lives in two places — the user document and, for anyone who has one, their Sync Gateway
 * account. Setting only the first moved the login while leaving every device replicating as that user on
 * the old secret, silently. The backend now changes both or neither, so "neither" needs saying.
 */
const SAVE_ERRORS = {
  SYNC_GATEWAY_UNAVAILABLE: 'Vi får ikke endret passordet akkurat nå. Ingenting er lagret — prøv igjen '
    + 'om litt, og kontakt support@cagista.com hvis det vedvarer.',
  SYNC_GATEWAY_FAILED: 'Vi får ikke endret passordet akkurat nå. Ingenting er lagret — prøv igjen '
    + 'om litt, og kontakt support@cagista.com hvis det vedvarer.',
  NOT_ALLOWED: 'Denne brukeren kan ikke få passordet endret her.',
};

export default {
  components: {LoadingSpinner},
  data() {
    return {
      loading: true,

      userId: '',
      saveError: '',
      confirmPassword: "",
      validPin: true,
      validEmail: true,

      user: {
        type: "user",
        merchant: "",
        firstname: "",
        lastname: "",
        userRole: "",
        username: "",
        userPassword: "",
        pincode: "",
        email: "",
        phone: "",
        status: "ACTIVE"
      }
    };
  },
  mounted() {
    this.userId = this.$route.query.userId;
    UserService.getUser(this.userId).then(
        (response) => {
          this.user = response.data;
          // The server deliberately does not return the password. Both fields stay empty, which means
          // "keep the current password" — the previous version pre-filled them with the stored value,
          // so anything typed there replaced a credential the admin could not actually read.
          this.user.userPassword = '';
          this.confirmPassword = '';
          //console.log('user: ' + JSON.stringify(this.user));

          this.loading = false;
        },
        (error) => {
          console.log(error);
        }
    );
  },
  computed: {
    activateSaveButton(){
      //console.log('user: ' + JSON.stringify(this.user));
      // On EDIT the password is optional — blank means "unchanged". It only has to match its
      // confirmation when one was actually typed. (Creating a user still requires one; see UserCreate.)
      if(this.user.firstname.length > 0 && this.user.lastname.length > 0 && this.user.userRole.length > 0 && this.user.username.length > 0
          && (this.user.userRole === 'ACCOUNTANT' || this.user.pincode.toString().length == 4) && this.user.email.length > 0 && this.user.phone.toString().length > 0
          && this.user.userPassword == this.confirmPassword && this.validEmail && this.validPin
      ){
        return true;
      } else {
        return false;
      }
    }
  },
  methods: {
    isSuperAdmin(){
      return UserUtil.isSuperAdmin();
    },
    checkUserPincode(event){
      this.validPin = true; //reset it

      //The field is type="text" (a pincode is a digit STRING — type="number" bound it as a Number and
      //that number, written to the user document, breaks the login of the person being edited). So the
      //digits-only rule that type="number" used to give for free has to be enforced here.
      const digits = event.target.value.replace(/\D/g, '').slice(0, 4);
      if (digits !== event.target.value) {
        event.target.value = digits;
      }
      this.user.pincode = digits;

      console.log('pincode: ' + this.user.pincode);
      if(event.target.value.length == 4){
        //console.log('Check Pin');
        UserService.checkUserPincode(this.user.pincode).then(
            (response) => {
              let result = response.data;
              if(result === 'TRUE'){
                //result := true -> pincode exists
                //console.log(result + ": exists -> Ugyldig");
                this.validPin = false;
              } else {
                //result := false -> pincode doesn't exist
                //console.log(result + ": not exists -> Gyldig");
                this.validPin = true;
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
            }
        );
      }

    },
    validateEmail(){
        var re = /\S+@\S+\.\S+/;
        let validatedResult = re.test(this.user.email);
        console.log('validatedResult: ' + validatedResult);
        this.validEmail = validatedResult;
    },
    selectFile(){
      this.imageFile = this.$refs.file.files[0];
      console.log("imageFile: " + this.imageFile.name)
    },
    saveUser(){
      this.loading = true;

      this.user.userId = this.userId;
      this.user.updatedDateTime = (new Date()).toISOString();
      if(this.user.userRole === 'ACCOUNTANT'){
        this.user.pincode = ''; //Accountant has no POS pincode; clear any stale value before saving.
      }
      this.editUser(this.user);
    },
    editUser(user){
      // Belt and braces with the input types above: these two fields are digit STRINGS, and a number
      // here is not cosmetic. UserDocumentConverter reads the user document on every authenticated
      // request; a numeric phone made it throw, which locked the merchant out of Backoffice entirely.
      // UserCreate has always coerced here — UserEdit did not, so editing a user broke their login.
      user.phone = user.phone === null || user.phone === undefined ? '' : String(user.phone);
      user.pincode = user.pincode === null || user.pincode === undefined ? '' : String(user.pincode);

      // Only send a password when one was typed; the server treats an absent field as "unchanged".
      // Emptiness is checked WITHOUT trimming, because a space is a legitimate password character.
      const payload = Object.assign({}, user);
      if(payload.userPassword === '' || payload.userPassword === null || payload.userPassword === undefined){
        delete payload.userPassword;
      }

      console.log('user: ' + JSON.stringify(Object.assign({}, payload, { userPassword: undefined })));
      this.saveError = '';
      UserService.editUser(payload).then(
          (response) => {
            const data = response.data;
            // A successful save answers with the document id — a bare string. A refused one answers with
            // an OBJECT, which is why this checks the type rather than truthiness: `if (data)` was true
            // for both, so a refusal navigated away as though it had saved. Refusals only became
            // possible once a password change had to move the Sync Gateway account too, and can fail
            // there.
            if (typeof data === 'string' && data) {
              this.$router.push({ path: 'users'});
            } else {
              const code = data && data.code;
              this.saveError = SAVE_ERRORS[code] || 'Brukeren ble ikke lagret. Prøv igjen, og kontakt '
                + 'support@cagista.com hvis det vedvarer.';
            }
            this.loading = false;
          },
          (error) => {
            console.log(error);
            this.loading = false;
            this.saveError = 'Brukeren ble ikke lagret. Prøv igjen, og kontakt support@cagista.com '
              + 'hvis det vedvarer.';
          }
      );
    }
  }
};
</script>
